/**
 * Les questions à choix de la section « lieu de la formation » d'une demande
 * INTRA : stationnement du formateur, emplacement du bac à flamme, emplacement
 * du camion en unité mobile, horaires de la formation.
 *
 * UNE SEULE SOURCE, POUR SIX ENDROITS QUI RÉPÉTAIENT LES MÊMES PHRASES. Chaque
 * libellé vivait cinq fois : les trois écrans qui posent ou relisent la question
 * — le formulaire public, la fiche demande et son instantané v1 — déclaraient la
 * même liste mot pour mot, et les deux fiches réécrivaient encore les mêmes
 * textes dans une cascade de `v-if`. Les valeurs, elles, une sixième fois côté
 * serveur.
 *
 * Ce n'est pas une affaire de goût. Ajouter la réponse « Je ne sais pas »
 * demandait six modifications coordonnées, et il en manquait une : la cascade
 * de l'instantané v1 n'avait pas de branche pour la valeur nouvelle, donc une
 * demande répondue « Je ne sais pas » s'affichait « Non renseigné » — dans
 * l'interface encore servie par défaut. Une liste lue au lieu d'être recopiée
 * rend cette faute impossible : ce qui n'est pas dans la liste n'a pas de
 * libellé, et la fiche dit alors « Non renseigné » à juste titre.
 *
 * Partagé entre le serveur (validation de la route publique, qui n'est pas
 * authentifiée) et le client (saisie et relecture).
 */

export const STATIONNEMENT_FORMATEUR_OPTIONS = [
  { value: 'parking_prive', label: 'Parking privé de la structure' },
  { value: 'parking_payant', label: 'Parking payant à proximité' },
  { value: 'stationnement_gratuit', label: 'Stationnement gratuit à proximité' },
]

export const BAC_FLAMME_OPTIONS = [
  { value: 'dispose', label: 'Je dispose d\'un emplacement extérieur de 10m² (herbe, parking etc.)' },
  { value: 'ne_dispose_pas', label: 'Je ne dispose pas d\'un emplacement extérieur' },
  // Troisième réponse : la personne qui remplit la demande n'est pas toujours
  // celle qui connaît les lieux. Sans elle, elle cochait au hasard l'une des
  // deux autres, et le formateur découvrait sur place que le bac à flamme
  // n'avait nulle part où aller.
  { value: 'ne_sait_pas', label: 'Je ne sais pas' },
]

export const CAMION_UNITE_MOBILE_OPTIONS = [
  { value: 'propriete_entreprise', label: 'Stationnement sur la propriété de l\'entreprise' },
  { value: 'voie_publique', label: 'Stationnement sur la voie publique' },
]

/**
 * Le libellé d'une valeur enregistrée, ou `null` quand la valeur ne figure pas
 * dans la liste — une demande jamais répondue, ou une valeur retirée du
 * catalogue depuis. C'est `null` que les fiches traduisent en « Non renseigné ».
 */
export function libelleOption(options, valeur) {
  return options.find((option) => option.value === valeur)?.label ?? null
}

/** Les valeurs qu'une liste accepte, pour ce qui doit filtrer avant d'écrire. */
export function valeursAutorisees(options) {
  return new Set(options.map((option) => option.value))
}

/**
 * « Avec horaires de nuit » et non « formation de nuit » : une session de 19h
 * à 22h comporte bien une partie nocturne sans se dérouler entièrement la nuit.
 * La journée reste le cas courant, donc la réponse par défaut.
 */
export const HORAIRES_FORMATION_OPTIONS = [
  { value: false, label: 'Formation en journée' },
  { value: true, label: 'Formation avec horaires de nuit (entre 21h00 et 06h00)' },
]

/**
 * La phrase que lisent la fiche, le tunnel et le devis. Rend `null` pour une
 * formation en journée : il n'y a alors rien à signaler.
 *
 * Le nombre de groupes concernés ne se dit qu'au-delà d'un groupe — avec un
 * seul, c'est forcément lui, et l'écrire serait du bruit.
 */
export function libelleHorairesNuit({ horairesNuit, nombreGroupes, groupesConcernes } = {}) {
  if (!horairesNuit) return null
  const total = Number(nombreGroupes) || 0
  // Le nombre de groupes du devis peut redescendre après coup (l'éditeur le
  // recalcule depuis la quantité facturée) : sans ce plafond, la phrase
  // annoncerait « 3 groupes sur 2 concernés ».
  const concernes = Math.min(Number(groupesConcernes) || 0, total)
  if (total > 1 && concernes > 0) {
    return `Horaires de nuit (21h-6h) : ${concernes} groupe${concernes > 1 ? 's' : ''} sur ${total} concerné${concernes > 1 ? 's' : ''}`
  }
  return 'Horaires de nuit (21h-6h)'
}
